import { View } from 'backbone.marionette';
import Backbone from 'backbone';

import StringCell from '@/backgrid/StringCell';
import DollarCell from '@/backgrid/DollarCell';

import OrderSummaryGridView from '../../../views/OrderSummaryGridView/OrderSummaryGridView';

import tmplDefaultSummaryView from './DefaultSummaryView.hbs';
import './DefaultSummaryView.css';

const DefaultSummaryView = View.extend({
  template: tmplDefaultSummaryView,

  className: 'default-summary-view',

  ui: {
    deviceOrderTotalsGrid: '.device-order-totals-grid',
    additionalAccessoriesOrderTotalsGrid: '.additional-accessories-order-totals-grid',
    additionalServicesOrderTotalsGrid: '.additional-services-order-totals-grid',
    summaryGrid: '.summary-grid',
    accessoriesContainer: '#accessories-container',
    servicesContainer: '#services-container',
    summaryContainer: '#summary-container'
  },

  regions: {
    deviceOrderTotalsGrid: '@ui.deviceOrderTotalsGrid',
    additionalAccessoriesOrderTotalsGrid: '@ui.additionalAccessoriesOrderTotalsGrid',
    additionalServicesOrderTotalsGrid: '@ui.additionalServicesOrderTotalsGrid',
    summaryGrid: '@ui.summaryGrid'
  },

  templateContext(){
    const showCancellationFees = this.model.get('orderTypeId') >= '22' && this.model.get('orderTypeId') <= '26';
    return {
      showCancellationFees
    }
  },

  onRender() {
    this.showDeviceOrderTotalsGrid();

    //Japan and Europe/Middle East Regions 1-4 forms have accessories.
    const showAccessoriesSummary = ['22', '23', '24', '25', '26'].includes(this.model.get('orderTypeId'));
    if (showAccessoriesSummary) {
      this.showAdditionalAccessoriesOrderTotalsGrid();
    } else {
      this.ui.accessoriesContainer.hide();
    }

    //Only Japan form has services.
    const showServicesSummary = this.model.get('orderTypeId') === '22';
    if (showServicesSummary) {
      this.showAdditionalServicesOrderTotalsGrid();
    } else {
      this.ui.servicesContainer.hide();
    }

    this.showSummaryGrid();
  },

  buildSummaryGridColumns(itemType) {
    const isItemGrid = itemType !== 'all';

    return [
      {
        name: 'monthsLeftInFiscalYearDescription',
        label: '',
        cell: StringCell,
        headerCell: 'custom',
        width: 60,
        editable: false,
        renderable: !isItemGrid
      },
      {
        name: 'monthsUntilDateEquipmentReq',
        label: '',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: !isItemGrid
      },
      {
        name: 'item',
        label: '',
        cell: StringCell,
        headerCell: 'custom',
        width: 60,
        editable: false,
        renderable: true
      },
      {
        name: 'pricePerItem',
        label: 'Unit Cost',
        cell: DollarCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: isItemGrid
      },
      {
        name: 'quantity',
        label: 'Quantity',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: isItemGrid
      },
      {
        name: 'totalPrice',
        label: itemType === 'devices' ? 'Total Monthly Cost' : 'Total Cost',
        cell: DollarCell,
        headerCell: 'custom',
        width: 15,
        editable: false,
        renderable: true
      }
    ];
  },

  showDeviceOrderTotalsGrid() {
    const devicesCollection = this.model.get('order').devicesSummary;
    devicesCollection.push({
      item: 'Total of MFD Devices',
      quantity: this.model.get('order').deviceTotalQuantity,
      totalPrice: this.model.get('order').deviceTotalMonthlyCost
    });
    this.deviceOrderTotalsCollection = new Backbone.Collection(devicesCollection);

    this.deviceOrderTotalsGridView = new OrderSummaryGridView({
      model: this.model,
      collection: this.deviceOrderTotalsCollection,
      columns: this.buildSummaryGridColumns('devices')
    });

    this.showChildView('deviceOrderTotalsGrid', this.deviceOrderTotalsGridView);
  },

  showAdditionalAccessoriesOrderTotalsGrid() {
    const accessoriesCollection = this.model.get('order').accessoriesSummary;
    accessoriesCollection.push({
      item: 'Total of Additional Accessories',
      quantity: this.model.get('order').accessoriesQuantity,
      totalPrice: this.model.get('order').accessoriesTotalCost
    });
    this.accessoriesOrderTotalsCollection = new Backbone.Collection(accessoriesCollection);

    this.additionalAccessoriesOrderTotalsGridView = new OrderSummaryGridView({
      model: this.model,
      collection: this.accessoriesOrderTotalsCollection,
      columns: this.buildSummaryGridColumns('accessories')
    });

    this.showChildView('additionalAccessoriesOrderTotalsGrid', this.additionalAccessoriesOrderTotalsGridView);
  },

  showAdditionalServicesOrderTotalsGrid() {
    const servicesCollection = this.model.get('order').servicesSummary;
    servicesCollection.push({
      item: 'Total of Additional MFD One-Time Services/Parts',
      quantity: this.model.get('order').servicesQuantity,
      totalPrice: this.model.get('order').servicesTotalCost
    });
    this.servicesOrderTotalsCollection = new Backbone.Collection(servicesCollection);

    this.additionalServicesOrderTotalsGridView = new OrderSummaryGridView({
      model: this.model,
      collection: this.servicesOrderTotalsCollection,
      columns: this.buildSummaryGridColumns('services')
    });

    this.showChildView('additionalServicesOrderTotalsGrid', this.additionalServicesOrderTotalsGridView);
  },

  showSummaryGrid() {
    this.summaryCollection = new Backbone.Collection([
      {
        item: 'One-Time Charges for Additional Accessories and Services',
        totalPrice: this.model.get('order').orderTotalsSummary.oneTimeCharges
      },
      {
        item: 'Recurring Monthly Cost (monthly charges)',
        totalPrice: this.model.get('order').orderTotalsSummary.recurringMonthlyCost
      },
      {
        monthsLeftInFiscalYearDescription: 'Number of months from Date Equipment Required through March 31, 2027',
        monthsUntilDateEquipmentReq: this.model.get('order').orderTotalsSummary.monthsUntilDateEquipmentReq,
        item: 'Total funding amount for this order',
        totalPrice:
          this.model.get('order').orderTotalsSummary.totalMiprAmount === '0'
            ? ''
            : this.model.get('order').orderTotalsSummary.totalMiprAmount
      },
      {
        item: 'Total Annual Cost',
        totalPrice: this.model.get('order').orderTotalsSummary.totalAnnualCost
      }
    ]);

    this.summaryGridView = new OrderSummaryGridView({
      model: this.model,
      collection: this.summaryCollection,
      columns: this.buildSummaryGridColumns('all')
    });

    this.showChildView('summaryGrid', this.summaryGridView);
  },

  isLeaseOrder() {
    switch (this.model.get('orderTypeId')) {
      case '22':
      case '23':
      case '24':
      case '25':
      case '26':
        return true;

      default:
        return false;
    }
  }
});

export default DefaultSummaryView;
